import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const privacyContent = `
Política de Privacidad

Última actualización: Abril 2026

1. Información que Recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital, incluyendo tu nombre y dirección de correo electrónico.

2. Uso de la Información
Tu información personal se utiliza exclusivamente para:
• Procesar y confirmar tu compra.
• Enviarte el enlace de descarga del producto digital.
• Comunicarnos contigo en caso de ser necesario respecto a tu pedido.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Compartición de Datos
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios de pago que procesan tu transacción de forma segura.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies.

6. Tus Derechos
Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de los canales disponibles.

7. Cambios en esta Política
Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página.
`.trim();
const termsContent = `
Términos y Condiciones

Última actualización: Abril 2026

1. Aceptación de los Términos
Al realizar una compra en este sitio, aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguna parte, te recomendamos no realizar la compra.

2. Producto Digital
El producto ofrecido es una guía digital descargable en formato electrónico. No se trata de un producto físico. El acceso se proporciona de forma inmediata tras la confirmación del pago.

3. Uso Personal
El contenido adquirido es para uso personal y no comercial. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa.

4. Descargo de Responsabilidad Médica
Este producto es únicamente informativo y educativo. No sustituye el consejo, diagnóstico o tratamiento médico profesional. Siempre consulta a tu médico antes de iniciar cualquier cambio en tu alimentación o estilo de vida.

5. Propiedad Intelectual
Todo el contenido del producto, incluyendo textos, gráficos y diseño, está protegido por derechos de autor. La compra otorga una licencia de uso personal, no la transferencia de propiedad intelectual.

6. Limitación de Responsabilidad
No garantizamos resultados específicos. Los resultados pueden variar según cada persona. No nos hacemos responsables de decisiones tomadas basándose en el contenido del producto.

7. Política de Reembolso
Debido a la naturaleza digital del producto y su entrega inmediata, no se aceptan devoluciones una vez completada la descarga.

8. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor desde su publicación en el sitio.
`.trim();
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: ModalProps) {
  return <AnimatePresence data-id="element-21">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-sm" onClick={onClose} data-id="element-22">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-23">
            <div className="flex items-center justify-between p-6 border-b border-cream-dark" data-id="element-24">
              <h2 className="text-2xl font-bold text-sage m-0" data-id="element-25">{title}</h2>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-cream-dark transition-colors text-charcoal-light" aria-label="Cerrar" data-id="element-26">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-27">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" data-id="element-28" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-29">
              <div className="whitespace-pre-line text-charcoal-light leading-relaxed text-sm" data-id="element-30">
                {content}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function FooterSection() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="bg-sage-dark text-cream/60 py-8 text-center text-sm" data-id="element-31">
        <div className="max-w-3xl mx-auto px-6" data-id="element-32">
          <div className="flex items-center justify-center gap-4 mb-4" data-id="element-33">
            <button onClick={() => setActiveModal('privacy')} className="text-cream/60 hover:text-gold transition-colors underline underline-offset-2 bg-transparent border-none cursor-pointer text-sm" data-id="element-34">
              Política de Privacidad
            </button>
            <span className="text-cream/30" data-id="element-35">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream/60 hover:text-gold transition-colors underline underline-offset-2 bg-transparent border-none cursor-pointer text-sm" data-id="element-36">
              Términos y Condiciones
            </button>
          </div>
          <p data-id="element-37">
            © {new Date().getFullYear()} Menopausia Sin Niebla. Todos los
            derechos reservados.
          </p>
          <p className="mt-2 text-xs" data-id="element-38">
            Este producto no sustituye el consejo médico profesional. Consulte a
            su médico antes de comenzar cualquier nuevo régimen nutricional.
          </p>
        </div>
      </footer>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" content={privacyContent} data-id="element-39" />
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" content={termsContent} data-id="element-40" />
    </>;
}